import { Form, formProviderHOC } from '@sravni/cosago-react-library/lib/components';
import { useFormContext } from '@sravni/cosago-react-library/lib/hooks';
import type { FormComponentProps } from '@sravni/cosago-react-library/lib/types';
import { useIsMobile } from '@sravni/react-utils';
import type { FC } from 'react';
import React, { useEffect } from 'react';

import { useAppSelector } from 'shared/lib';
import { sendEventShowStep } from 'shared/lib/sendGAEvents';
import { useCommonFormBodyHooks } from 'shared/lib/useCommonFormBodyHooks';
import { useSyncFormFieldsToQueryParams } from 'shared/lib/useSyncFormFieldsToQueryParams/useSyncFormFieldsToQueryParams';
import type { TCommonAdditionalFormProps } from 'shared/types';
import { FormStickyButton } from 'shared/ui';
import { FormWithControlledNavigation } from 'shared/ui/FormWithControlledNavigation';

import { useSendFieldErrorAnalyticsEvent } from 'entities/creditForm/lib/useSendFieldErrorAnalyticsEvent';
import { wlBlockFFSelector } from 'entities/whiteLabels';
import type { TCreditPurposeFormFields } from 'src/client/entities/creditForm';
import { useDevFillMockDataTool } from 'src/client/entities/creditForm/lib/useDevFillMockDataTool';

import { CreditPurposeInfoTexts } from 'features/UpdateCreditPurposeInfo';
import { FormFieldsNavigationSequence } from 'features/UpdateCreditPurposeInfo/CreditPurposeInfo.config';
import { useCarInfoDictionariesLoader } from 'features/UpdateCreditPurposeInfo/lib/useCarInfoDictionariesLoader';
import { useIsShowAutoFields } from 'features/UpdateCreditPurposeInfo/lib/useIsShowAutoFields';

import { useAccordionHeaderParams } from '../../lib/useAccordionHeaderParams';

type TCreditPurposeInfoFormView = TCommonAdditionalFormProps & {
  shouldRestoreAdditionalData: boolean;
};

type Props = FormComponentProps<TCreditPurposeFormFields, TCreditPurposeInfoFormView>;

const CreditPurposeInfoFormView: FC<Props> = ({
  isDialog,
  onSubmit,
  NAMES,
  submitButtonText,
  FieldConstructor,
  isLoading,
  additionalProps,
}) => {
  const { setHeader, isFormForceOpened, formId, isAnyFormStepOpenedInPopup } = additionalProps || {};

  const { watch } = useFormContext<TCreditPurposeFormFields>();
  const isMobile = useIsMobile();

  const [title, subtitle] = useAccordionHeaderParams(watch(), {}, false);

  useEffect(() => {
    sendEventShowStep(1, 'Цель кредита');
  }, []);

  useSyncFormFieldsToQueryParams({
    amount: watch('amount'),
    period: watch('period.value'),
    purpose: watch('purpose.value'),
  });

  useCarInfoDictionariesLoader();

  useSendFieldErrorAnalyticsEvent<TCreditPurposeFormFields>(1, CreditPurposeInfoTexts);
  useDevFillMockDataTool<TCreditPurposeFormFields>({
    type: 'creditPurpose',
    stepLabel: 'Цель кредита',
  });

  const { controls } = useCommonFormBodyHooks({
    isFormForceOpened,
    currentControls: FormFieldsNavigationSequence,
    headerData: {
      title,
      subtitle,
    },
    setHeader,
  });

  const blockWllFF = useAppSelector(wlBlockFFSelector);

  const { isShowAutoYearField, isShowAutoModelField, isShowAutoFields } = useIsShowAutoFields();
  const isStickyButton = !isDialog && isMobile && !isAnyFormStepOpenedInPopup;

  return (
    <FormWithControlledNavigation
      formId={formId}
      onSubmit={onSubmit}
      controlElement={FieldConstructor}
      isDialog={isDialog}
      isLoading={isLoading}
      submitButtonText={submitButtonText}
      navigationControls={controls}
      withSubmitButtons={!isStickyButton}
    >
      <Form.Block>
        <Form.Row>
          {!blockWllFF?.withoutAdditionalFilters && <FieldConstructor type={NAMES.purpose} />}

          <FieldConstructor type={NAMES.amount} />

          {!blockWllFF?.withoutAdditionalFilters && <FieldConstructor type={NAMES.period} />}
        </Form.Row>
        {isShowAutoFields && (
          <>
            <Form.Row>
              <FieldConstructor type={NAMES.initialFee} />

              <FieldConstructor type={NAMES.autoBrand} />

              {isShowAutoModelField && <FieldConstructor type={NAMES.autoModel} />}
            </Form.Row>
            {isShowAutoYearField && (
              <Form.Row>
                <FieldConstructor type={NAMES.autoYear} />
              </Form.Row>
            )}
          </>
        )}
      </Form.Block>
      <FormStickyButton
        onClick={onSubmit}
        enabled={isStickyButton}
      >
        {submitButtonText}
      </FormStickyButton>
    </FormWithControlledNavigation>
  );
};

export const CreditPurposeInfoViewHoc = formProviderHOC<TCreditPurposeFormFields, TCreditPurposeInfoFormView>(
  CreditPurposeInfoFormView,
);
